import { rest } from '@ax2/common/index';
import SurveySchema from '@ax2/common/model/schemas/SurveySchema';
import UUID from '@ax2/common/model/UUID';
import PanelType from '@ax2/common/model/PanelType';
import { SlotTO } from 'model/SlotTO';
import QuestionResults from 'model/reports/QuestionResults';
import { AnswersCount } from 'model/reports/AnswersCount';
import { AuthResponseTO } from 'model/AuthResponseTO';

export function getSlots(): Promise<SlotTO[]> {
  return rest.api<SlotTO[]>('/payment/v1/slots', {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf8',
    },
  });
}

export function getSurveys(): Promise<SurveySchema[]> {
  return rest.api<SurveySchema[]>('/makesurvey/v1/surveys', {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf8',
    },
  });
}

export function createQuestion(surveyId: UUID): Promise<UUID> {
  const url = `/makesurvey/v1/survey/${surveyId}/questions`;
  return rest.api<UUID>(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf8',
    },
  });
}
export function createQuestionFromTemplate(
  surveyId: UUID,
  questionTemplateId: UUID
): Promise<UUID> {
  const url = `/makesurvey/v1/survey/${surveyId}/questions?questionTemplateId=${questionTemplateId}`;
  return rest.api<UUID>(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf8',
    },
  });
}
export function createSurvey(
  panel: PanelType,
  specialty?: number
): Promise<UUID> {
  const specialtyParam = specialty ? `&specialty=${specialty}` : '';
  const url = `/makesurvey/v1/survey?panel=${panel}${specialtyParam}`;
  return rest.api<UUID>(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf8',
    },
  });
}

export function createSurveyFromTemplate(
  panel: PanelType,
  surveyTemplateId: UUID,
  specialty?: number
): Promise<UUID> {
  const specialtyParam = specialty ? `&specialty=${specialty}` : '';
  const url = `/makesurvey/v1/survey?panel=${panel}&surveyTemplateId=${surveyTemplateId}${specialtyParam}`;
  return rest.api<UUID>(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf8',
    },
  });
}

export function createSurveyFromExisting(
  existingSurveyId: UUID
): Promise<UUID> {
  const url = `makesurvey/v1/survey/${existingSurveyId}/copy`;
  return rest.api<UUID>(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf8',
    },
  });
}

export function storeSurvey(
  survey: SurveySchema,
  isValid: boolean
): Promise<void> {
  const url = `/makesurvey/v1/survey/${survey.id}?isValid=${isValid}`;
  return rest.api<void>(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf8',
    },
    data: JSON.stringify(survey),
  });
}

export function markActive(surveyId: UUID): Promise<void> {
  const url = `/makesurvey/v1/survey/${surveyId}/markActive`;
  return rest.api<void>(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf8',
    },
  });
}
export function launchSurvey(surveyId: UUID): Promise<void> {
  const url = `/makesurvey/v1/survey/${surveyId}/launch`;
  return rest.api<void>(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf8',
    },
  });
}

export function getAnswersCount(surveyId: UUID): Promise<AnswersCount> {
  return rest.api<AnswersCount>(
    `/makesurvey/v1/survey/${surveyId}/answercount`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json; charset=utf8',
      },
    }
  );
}

export function getResults(surveyId: UUID): Promise<QuestionResults[]> {
  return rest.api<QuestionResults[]>(
    `/makesurvey/v1/survey/${surveyId}/results`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json; charset=utf8',
      },
    }
  );
}
export function downloadXlsxResults(surveyId: UUID): Promise<Blob> {
  return rest
    .apiResponse(`/makesurvey/v1/survey/${surveyId}/results/xlsx`, {
      method: 'GET',
      responseType: 'blob',
      headers: {
        Accept:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'Content-Type':
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet; charset=utf8',
      },
    })
    .then(response => {
      return new Blob([response.data], {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet; charset=utf8',
      });
    });
}

export function downloadXlsxAnswers(surveyId: UUID): Promise<Blob> {
  return rest
    .apiResponse(`/makesurvey/v1/survey/${surveyId}/answers/xlsx`, {
      method: 'GET',
      responseType: 'blob',
      headers: {
        Accept:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'Content-Type':
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet; charset=utf8',
      },
    })
    .then(response => {
      return new Blob([response.data], {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet; charset=utf8',
      });
    });
}

export function deleteSurvey(surveyId: UUID): Promise<void> {
  const url = `/makesurvey/v1/survey/${surveyId}`;
  return rest.api<void>(url, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf8',
    },
  });
}

export function fetchImage(key: string): Promise<Blob> {
  return rest
    .apiResponse(`/makesurvey/v1/image/${key}`, {
      method: 'GET',
      responseType: 'blob',
    })
    .then(response => {
      return new Blob([response.data], {
        type: 'image/*',
      });
    });
}

export function uploadImage(key: string, file: File): Promise<any> {
  const formData = new FormData();
  formData.append('file', file);
  return rest.api(`/makesurvey/v1/image/${key}`, {
    method: 'POST',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

export function deleteImage(key: string): Promise<any> {
  return rest.api(`/makesurvey/v1/image/${key}`, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf8',
    },
  });
}

export function getMetrics(data: {
  page: string;
  email?: string;
  panel?: string;
  status?: string;
}): Promise<any> {
  const { page, email, panel, status } = data;
  let url = `/makesurvey/v1/project-metrics-data?page=${page}`;
  if (email) url += `&email=${email}`;
  if (panel) url += `&panel=${panel}`;
  if (status) url += `&status=${status}`;
  return rest.api<any[]>(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf8',
    },
  });
}

export function getAllClients(data: {
  page: string;
  email: string | null;
  isAdmin: boolean | null;
}): Promise<AuthResponseTO[]> {
  const { page, email, isAdmin } = data;
  let url = `/client/profiles?page=${page}`;
  if (email) url += `&email=${email.toLowerCase()}`;
  if (typeof isAdmin === 'boolean') url += `&isAdmin=${isAdmin}`;
  return rest.api<AuthResponseTO[]>(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });
}

export function toggleAdmin(id: UUID, isAdmin: boolean): Promise<any> {
  return rest.api(`/client/update/${id}?isAdmin=${isAdmin}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });
}

export function setQuotas(
  panel1: string,
  value1: number,
  panel2: string,
  value2: number,
  surveyId: UUID
): Promise<void> {
  const url = `/makesurvey/v1/set-quotas?panel1=${panel1}&value1=${value1}&panel2=${panel2}&value2=${value2}&surveyId=${surveyId}`;
  return rest.api<void>(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf8',
    },
  });
}
